import { create } from 'zustand'
import type { QueryKey } from '@tanstack/react-query'
import type { AnnotationDocument } from '@/lib/annotation/schema'

/**
 * Ephemeral per-session optimistic state for uploaded-photo rotate.
 *
 * Keyed by MediaObject id. Survives lightbox close/reopen within the
 * same app session so a user who closes the lightbox the instant after
 * tapping rotate (before the server-side commit lands) reopens to the
 * same rotated bitmap rather than a snap-back to the canonical.
 *
 * Resets on app restart. **Never writes blob URLs into React Query or
 * any persisted cache** — those have a 7-day TTL via IndexedDB and
 * blob URLs die on page reload, so persisting them would resurrect
 * dead URLs and break thumbnails. The whole point of this store is to
 * keep the optimistic ephemera OUT of the persisted cache layer.
 *
 * The matching server-confirmation flow (PR 3):
 *   1. Lightbox commits pending rotate via POST `/media_objects/{id}/rotate`.
 *      The body carries a `rotationRequestUUID` generated at commit time.
 *   2. Backend rotates `displayLarge` + thumbnails, leaves `canonicalEvidence`
 *      byte-identical, publishes both:
 *        a. authoritative parent Issue/Comment payload (cache-busts URLs)
 *        b. `/users/{uid}/bg_sync/media_objects/rotate/{rotationRequestUUID}` echo
 *   3. `useMediaObjectRotationSubscriber` refreshes the exact parent
 *      query after the matching echo, then calls `clearForRequest(uuid)`.
 *      A bounded watchdog performs the same refresh if the echo is lost.
 *      Until either path completes, the renderer keeps the optimistic URL.
 */

export type RotationStatus = 'idle' | 'committing' | 'reconciling'

export type RotationEntry = {
  /** Net pending degrees mod 360. 0 means "user tapped 4× — no commit needed". */
  pendingDegrees: 0 | 90 | 180 | 270
  /**
   * The PRISTINE canonical bitmap source (the un-rotated `item.src`),
   * captured on the FIRST tap and preserved unchanged across every
   * subsequent tap. Each tap canvas-rotates THIS source by the net
   * `pendingDegrees` rather than re-rotating the previous `blobUrl`, so
   * any number of taps produces only a single JPEG re-encode generation
   * (no progressive blur). It is a durable server URL — not a blob URL —
   * so it stays valid for the lifetime of the entry.
   */
  originalSrc: string
  /** Object URL of the locally canvas-rotated bitmap. Revoke on clear. */
  blobUrl: string | null
  /**
   * Annotation document with items already transformed via rotateItems.
   * Held here (not in React Query) so the lightbox overlay shows rotated
   * strokes while the server-side commit is in flight.
   */
  optimisticAnnotation: AnnotationDocument | null
  /**
   * UUID sent with the POST. Set at `commit` time; the matching event
   * on `/users/{uid}/bg_sync/media_objects/rotate/{rotationRequestUUID}` clears
   * this entry.
   */
  rotationRequestUUID: string | null
  /** Exact authoritative parent cache entry refreshed during reconciliation. */
  reconcileQueryKey: QueryKey | null
  /** True when the rotate POST returned a clean 2xx response. */
  serverAcknowledged: boolean
  status: RotationStatus
}

type LightboxRotationStore = {
  /** mediaObjectId → entry. Strings only — accepts both number and IRI shapes. */
  entries: Record<string, RotationEntry>

  /**
   * Apply a per-tap optimistic rotate locally. Net degrees are
   * accumulated mod 360 so 4×90 = 0 (commit-on-close will see 0 and
   * skip the POST entirely).
   */
  applyTap: (
    mediaObjectId: number | string,
    degreesIncrement: 90 | 180 | 270,
    next: {
      blobUrl: string
      optimisticAnnotation: AnnotationDocument | null
      /**
       * Pristine canonical source. Only consumed when CREATING the entry
       * (first tap); preserved from the existing entry on later taps so
       * every tap re-rotates the same original bytes.
       */
      originalSrc: string
      reconcileQueryKey?: QueryKey
    },
  ) => void

  /**
   * Replace `optimisticAnnotation` for an existing entry without
   * touching `blobUrl` or `pendingDegrees`. Called by
   * `EditMarkupInLightbox.handleSave` when the user saves markup
   * while a rotation is pending — the strokes the user just drew
   * live in the rotated coord space, and any code reading the
   * rotation store (notably `AttachmentType` for the thumbnail
   * overlay) needs to see them or it falls back to the now-stale
   * pre-edit document. No-op if the entry doesn't exist.
   */
  updateOptimisticAnnotation: (
    mediaObjectId: number | string,
    optimisticAnnotation: AnnotationDocument | null,
  ) => void

  /**
   * Mark an entry as committing (rotate POST in flight). The renderer
   * keeps showing the optimistic blobUrl through this state.
   */
  markCommitting: (mediaObjectId: number | string, rotationRequestUUID: string) => void

  /**
   * Move to reconciling after a clean 2xx or an opaque native response;
   * wait for the Mercure echo or watchdog refresh before clearing.
   */
  markReconciling: (mediaObjectId: number | string, serverAcknowledged: boolean) => void

  /**
   * Clear an entry by mediaObjectId. Called when the Mercure echo +
   * parent payload have both landed. Revokes the blobUrl.
   */
  clearForMediaObject: (mediaObjectId: number | string) => void

  /**
   * Same, but resolved via rotationRequestUUID — the Mercure echo
   * payload carries the UUID; the subscriber looks up which entry to
   * clear.
   */
  clearForRequest: (rotationRequestUUID: string) => void

  /** Read accessor — undefined when no rotation pending for this id. */
  get: (mediaObjectId: number | string) => RotationEntry | undefined
}

const key = (id: number | string): string => String(id)

const useLightboxRotationStore = create<LightboxRotationStore>((set, getState) => ({
  entries: {},

  applyTap: (mediaObjectId, degreesIncrement, next) =>
    set(state => {
      const k = key(mediaObjectId)
      const prior = state.entries[k]
      const priorBlob = prior?.blobUrl
      // Revoke the previous tap's blob URL before replacing — the user
      // hits rotate repeatedly and we don't want N orphaned blobs.
      if (priorBlob && priorBlob !== next.blobUrl && priorBlob.startsWith('blob:')) {
        URL.revokeObjectURL(priorBlob)
      }
      const priorNet = prior?.pendingDegrees ?? 0
      const sum = (priorNet + degreesIncrement) % 360
      // Normalize to {0, 90, 180, 270} — TS doesn't carry the mod
      // constraint so a narrow cast is fine.
      const pendingDegrees = sum as 0 | 90 | 180 | 270
      return {
        entries: {
          ...state.entries,
          [k]: {
            pendingDegrees,
            // Keep the pristine source from the first tap; only fall back
            // to the freshly-passed one when creating the entry.
            originalSrc: prior?.originalSrc ?? next.originalSrc,
            blobUrl: next.blobUrl,
            optimisticAnnotation: next.optimisticAnnotation,
            rotationRequestUUID: null,
            reconcileQueryKey: prior?.reconcileQueryKey ?? next.reconcileQueryKey ?? null,
            serverAcknowledged: false,
            status: 'idle',
          },
        },
      }
    }),

  updateOptimisticAnnotation: (mediaObjectId, optimisticAnnotation) =>
    set(state => {
      const k = key(mediaObjectId)
      const prior = state.entries[k]
      if (!prior) return state
      return {
        entries: {
          ...state.entries,
          [k]: { ...prior, optimisticAnnotation },
        },
      }
    }),

  markCommitting: (mediaObjectId, rotationRequestUUID) =>
    set(state => {
      const k = key(mediaObjectId)
      const prior = state.entries[k]
      if (!prior) return state
      return {
        entries: {
          ...state.entries,
          [k]: {
            ...prior,
            rotationRequestUUID,
            serverAcknowledged: false,
            status: 'committing',
          },
        },
      }
    }),

  markReconciling: (mediaObjectId, serverAcknowledged) =>
    set(state => {
      const k = key(mediaObjectId)
      const prior = state.entries[k]
      if (!prior) return state
      return {
        entries: {
          ...state.entries,
          [k]: { ...prior, serverAcknowledged, status: 'reconciling' },
        },
      }
    }),

  clearForMediaObject: mediaObjectId =>
    set(state => {
      const k = key(mediaObjectId)
      const prior = state.entries[k]
      if (!prior) return state
      if (prior.blobUrl && prior.blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(prior.blobUrl)
      }
      const next = { ...state.entries }
      delete next[k]
      return { entries: next }
    }),

  clearForRequest: rotationRequestUUID =>
    set(state => {
      const next: Record<string, RotationEntry> = {}
      for (const [k, e] of Object.entries(state.entries)) {
        if (e.rotationRequestUUID === rotationRequestUUID) {
          if (e.blobUrl && e.blobUrl.startsWith('blob:')) {
            URL.revokeObjectURL(e.blobUrl)
          }
          continue
        }
        next[k] = e
      }
      return { entries: next }
    }),

  get: mediaObjectId => getState().entries[key(mediaObjectId)],
}))

export default useLightboxRotationStore
